import type { ReactNode } from 'react'

export function Card({
  title,
  subtitle,
  actions,
  children,
  flush,
}: {
  title?: string
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  /** Drop the body padding — for cards that are entirely a table. */
  flush?: boolean
}) {
  return (
    <section className="card">
      {title !== undefined && (
        <h2>
          <span>{title}</span>
          {subtitle !== undefined && <span className="card-subtitle">{subtitle}</span>}
          {actions !== undefined && <span className="card-actions">{actions}</span>}
        </h2>
      )}
      {flush === true ? children : <div className="body">{children}</div>}
    </section>
  )
}

export function Pill({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'bad' | 'neutral'
  children: ReactNode
}) {
  return <span className={`pill ${tone}`}>{children}</span>
}

export function severityTone(severity: string): 'ok' | 'warn' | 'bad' | 'neutral' {
  if (severity === 'error') return 'bad'
  if (severity === 'warning') return 'warn'
  return 'neutral'
}

/** A single headline number. `dulled` marks figures we cannot fully substantiate. */
export function Stat({
  label,
  value,
  sub,
  tone,
  dulled,
  onClick,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: 'blue' | 'amber' | 'green' | 'plain'
  dulled?: boolean
  /** When set, the tile drills down. Rendered as a real button for keyboard users. */
  onClick?: () => void
}) {
  const className = `stat ${tone ?? 'plain'}${dulled === true ? ' dulled' : ''}`
  const inner = (
    <>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub !== undefined && <div className="stat-sub">{sub}</div>}
    </>
  )
  if (onClick === undefined) return <div className={className}>{inner}</div>
  return (
    <button type="button" className={`${className} stat-clickable`} onClick={onClick}>
      {inner}
    </button>
  )
}

export function StatRow({ children }: { children: ReactNode }) {
  return <div className="stat-row">{children}</div>
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <p className="muted" style={{ padding: 24 }}>{label}</p>
}

export function ErrorNote({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null
  const message = error instanceof Error ? error.message : String(error)
  return (
    <Card title="Something went wrong">
      <p>{message}</p>
    </Card>
  )
}

/** Downloads `rows` as a CSV file, quoting properly. */
export function toCsv(headers: string[], rows: (string | number | null)[][]): string {
  const escape = (v: string | number | null): string => {
    if (v === null) return ''
    const s = String(v)
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join(
    '\r\n',
  )
}

export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel opens UTF-8 correctly — these files go straight to finance.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function CsvButton({
  filename,
  headers,
  rows,
}: {
  filename: string
  headers: string[]
  rows: (string | number | null)[][]
}) {
  return (
    <button
      type="button"
      className="link-button"
      onClick={() => downloadCsv(filename, toCsv(headers, rows))}
      disabled={rows.length === 0}
    >
      Export CSV
    </button>
  )
}

/**
 * The files behind a figure. Every drawer lists the source files that fed it,
 * with the load date and what the loader rejected or stripped — connecting the
 * output on screen back to the extract someone sent us. Listing, not download:
 * some extracts carry resident personal data that is dropped at load, so the
 * originals are never served back out. Full ingest history is on the Data screen.
 */
